import { AvatarDropdown, AvatarName, Footer } from '@/components';
import { FileSearchOutlined, MessageFilled } from '@ant-design/icons';
import {
  PageLoading,
  type Settings as LayoutSettings,
} from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { Badge, Input } from 'antd';
import defaultSettings from '../config/defaultSettings';
import routes, { IRoute } from '../config/routes';
import LargeAvatar from './components/Profile/LargeAvatar';
import { AddDropdown } from './components/RightContent';
import { errorConfig } from './requestErrorConfig';
import { queryCurrentUser } from './services/apis/login';

const loginPath = '/user/login';

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  routes?: IRoute[];
  adminRoutes?: IRoute[];
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  // 获取管理员路由
  const adminRoutes = routes.filter((route) => route.path.includes('/admin'));
  const fetchUserInfo = async () => {
    try {
      const resp = await queryCurrentUser();
      return {
        ...resp.data.content,
      };
    } catch (error) {
      history.push(loginPath);
    }
    return undefined;
  };
  const { location } = history;
  // 如果不是登录页面，执行
  if (location.pathname !== loginPath) {
    // 获取用户信息
    const currentUser = await fetchUserInfo();
    return {
      routes,
      adminRoutes,
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  return {
    routes,
    adminRoutes,
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = (options) => {
  const { initialState } = options;

  return {
    disableMobile: true,
    // actionsRender: () => [<Question key="doc" />, <SelectLang key="SelectLang" />],
    // 顶部搜索框
    actionsRender: () => {
      if (location.pathname.includes('/admin')) {
        return [];
      }

      const MessageIcon = (
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: 5,
            marginRight: -10,
          }}
          key="notification"
          onClick={() => {
            history.push('/manage/workbench/todo');
          }}
        >
          <Badge dot>
            <MessageFilled style={{ fontSize: 20, color: '#ffffff' }} />
          </Badge>
        </div>
      );

      // 在搜索页面不显示搜索框
      if (location.pathname === '/search') {
        return [<AddDropdown key="add" />, MessageIcon];
      }
      return [
        <Input.Search
          key="search"
          size="middle"
          enterButton={<FileSearchOutlined />}
          placeholder="搜索课程,知识,用户"
          onSearch={(value) => {
            history.push(`/search?keyword=${value}`);
          }}
        />,
        <AddDropdown key="add" />,
        MessageIcon,
      ];
    },
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        if (location.pathname.includes('/admin')) {
          return null;
        }
        return <AvatarDropdown menu={true}>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      // content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    pageTitleRender: false,
    breadcrumbRender: false,
    collapsed: false,
    menuHeaderRender: undefined,
    menuExtraRender(props) {
      return null;
      if (props.location?.pathname?.includes('/admin')) {
        return null;
      }
      return <LargeAvatar />;
    },
    // 自定义 403 页面
    unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      if (initialState?.loading) {
        return <PageLoading />;
      }
      return children;
    },
    menu: {
      locale: false,
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,
};

// let timer: any;

// window.addEventListener('scroll', function () {
//   document.body.toggleAttribute('scroll', true);
//   if (timer) {
//     clearTimeout(timer);
//     timer = null;
//   }
//   timer = setTimeout(() => {
//     document.body.toggleAttribute('scroll');
//   }, 500);
// });
